'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { trackEvent } from '@/lib/analytics';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { contactCopy } from '@/lib/i18n/pages/contact';

function buildSchema(v: { nameMin: string; emailInvalid: string; messageMin: string }) {
  return z.object({
    name: z.string().min(2, v.nameMin),
    email: z.string().email(v.emailInvalid),
    message: z.string().min(10, v.messageMin),
  });
}

type ContactFormData = z.infer<ReturnType<typeof buildSchema>>;

export function ContactForm() {
  const { locale, dir } = useLocale();
  const isRtl = dir === 'rtl';
  const t = pageCopy(contactCopy, locale).form;
  const contactSchema = useMemo(() => buildSchema(t.validation), [t.validation]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(process.env.NEXT_PUBLIC_CONTACT_FUNCTION_URL || '/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackEvent('contact_submit', { status: 'success' });
        reset();
      } else {
        setSubmitStatus('error');
        trackEvent('contact_submit', { status: 'error' });
      }
    } catch {
      setSubmitStatus('error');
      trackEvent('contact_submit', { status: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: 'transparent',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
    color: '#ffffff',
    outline: 'none',
    transition: 'border-color 0.3s',
    fontFamily: 'inherit',
    textAlign: 'start' as const,
  };

  // Email is an LTR island: content and caret read left to right, and on RTL
  // pages the text hugs the right (start) edge to line up with the label.
  const emailStyle = isRtl ? { textAlign: 'right' as const } : {};

  const errorStyle = {
    fontSize: isRtl ? '0.875rem' : '0.75rem',
    color: '#ef4444',
    marginTop: '0.5rem',
    textAlign: 'start' as const,
  };

  const labelStyle = {
    fontSize: isRtl ? '13px' : '10px',
    letterSpacing: isRtl ? 'normal' : '0.3em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '0.25rem',
    display: 'block',
    textAlign: 'start' as const,
  };

  return (
    <div style={{ width: '100%' }}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '2rem' }}
      >
        {/* Name */}
        <div>
          <label htmlFor="name" style={labelStyle}>{t.nameLabel}</label>
          <input
            {...register('name')}
            type="text"
            id="name"
            dir="auto"
            autoComplete="name"
            placeholder={t.namePlaceholder}
            style={{
              ...inputStyle,
              borderBottomColor: errors.name ? '#ef4444' : 'rgba(255,255,255,0.15)',
            }}
            onFocus={(e) => { if (!errors.name) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.5)'; }}
            onBlur={(e) => { if (!errors.name) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)'; }}
          />
          {errors.name && (
            <p role="alert" style={errorStyle}>{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" style={labelStyle}>{t.emailLabel}</label>
          <input
            {...register('email')}
            type="email"
            id="email"
            placeholder={t.emailPlaceholder}
            dir="ltr"
            autoComplete="email"
            style={{
              ...inputStyle,
              ...emailStyle,
              borderBottomColor: errors.email ? '#ef4444' : 'rgba(255,255,255,0.15)',
            }}
            onFocus={(e) => { if (!errors.email) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.5)'; }}
            onBlur={(e) => { if (!errors.email) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)'; }}
          />
          {errors.email && (
            <p role="alert" style={errorStyle}>{errors.email.message}</p>
          )}
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" style={labelStyle}>{t.messageLabel}</label>
          <textarea
            {...register('message')}
            id="message"
            dir="auto"
            rows={5}
            placeholder={t.messagePlaceholder}
            style={{
              ...inputStyle,
              resize: 'none',
              borderBottomColor: errors.message ? '#ef4444' : 'rgba(255,255,255,0.15)',
            }}
            onFocus={(e) => { if (!errors.message) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.5)'; }}
            onBlur={(e) => { if (!errors.message) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)'; }}
          />
          {errors.message && (
            <p role="alert" style={errorStyle}>{errors.message.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div style={{ marginTop: '1rem' }}>
          <button
            type="submit"
            disabled={isSubmitting}
            className="group"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              backgroundColor: '#ffffff',
              color: '#0c0c0c',
              border: 'none',
              borderRadius: '100px',
              padding: '1.125rem 2.5rem',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'all 0.3s ease',
              width: '100%',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#e5e5e5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            <span
              style={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: isRtl ? 'normal' : '0.2em',
              }}
            >
              {isSubmitting ? t.sending : t.send}
            </span>
            <div
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                backgroundColor: '#0c0c0c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
              className="group-hover:rotate-45"
            >
              <svg className="rtl-mirror" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </button>
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              borderRadius: '8px',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
            }}
          >
            <p style={{ color: '#22c55e', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: isRtl ? 'normal' : '0.15em' }}>
              {t.success}
            </p>
          </div>
        )}
        {submitStatus === 'error' && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              borderRadius: '8px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <p style={{ color: '#ef4444', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: isRtl ? 'normal' : '0.15em' }}>
              {t.error}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
